import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// UAE specific constants
const DELIVERY_FEE = 30; // AED 30 delivery fee - always applied

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1) => {
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === product.id);

      if (existingItem) {
        return currentItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...currentItems, {
        id: product.id,
        name: product.name,
        nameAr: product.nameAr,
        price: product.price,
        salePrice: product.salePrice || null,
        onSale: product.onSale || false,
        image: product.image,
        hideImage: product.hideImage || false,
        quantity
      }];
    });
  };

  const removeItem = (productId) => {
    setItems(currentItems => currentItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems(currentItems =>
      currentItems.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getItemCount = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getDisplayPrice = (item) => {
    return (item.onSale && item.salePrice) ? item.salePrice : item.price;
  };

  const getSubtotal = () => {
    return items.reduce((total, item) => total + (getDisplayPrice(item) * item.quantity), 0);
  };

  const getShipping = () => {
    return DELIVERY_FEE; // Always AED 30
  };

  const getTotal = () => {
    return getSubtotal() + getShipping();
  };

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemCount,
    getSubtotal,
    getShipping,
    getTotal,
    getDisplayPrice,
    isEmpty: items.length === 0,
    DELIVERY_FEE
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
